import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatDateTime } from '@/lib/utils';

export interface SearchResult {
  type: 'church' | 'profile' | 'event' | 'audit' | 'report';
  id: string;
  label: string;
  sublabel: string;
  href: string;
  icon?: string;
}

export function useGlobalSearch(query: string): {
  data: SearchResult[] | undefined;
  isLoading: boolean;
  error: any;
} {
  return useQuery({
    queryKey: ["global_search", query],
    queryFn: async (): Promise<SearchResult[]> => {
      console.debug("[useGlobalSearch] searching for:", query);
      if (!query || query.length < 2) {
        return [];
      }

      const results: SearchResult[] = [];
      const searchTerm = `%${query}%`;

      try {
        // Search churches
        const { data: churches, error: churchesError } = await supabase
          .from('churches')
          .select('id, name, contact_email, phone')
          .or(`name.ilike.${searchTerm},contact_email.ilike.${searchTerm}`)
          .limit(7);

        if (!churchesError && churches) {
          churches.forEach(church => {
            results.push({
              type: 'church',
              id: church.id,
              label: church.name,
              sublabel: church.contact_email || church.phone || 'No contact info',
              href: `/church/${church.id}`,
              icon: 'Building2'
            });
          });
        }

        // Search profiles/members
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, email')
          .or(`first_name.ilike.${searchTerm},last_name.ilike.${searchTerm},email.ilike.${searchTerm}`)
          .limit(7);

        if (!profilesError && profiles) {
          profiles.forEach(profile => {
            results.push({
              type: 'profile',
              id: profile.id,
              label: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'No name',
              sublabel: profile.email || 'No email',
              href: `/directory/member/${profile.id}`,
              icon: 'User'
            });
          });
        }

        // Search events
        const { data: events, error: eventsError } = await supabase
          .from('events')
          .select('id, title, start_time, church_id')
          .ilike('title', searchTerm)
          .order('start_time', { ascending: false })
          .limit(7);

        if (!eventsError && events) {
          events.forEach(event => {
            results.push({
              type: 'event',
              id: event.id,
              label: event.title,
              sublabel: formatDateTime(event.start_time, 'No date', { dateStyle: 'medium' }),
              href: `/church/${event.church_id}/events`,
              icon: 'CalendarDays'
            });
          });
        }

        // Search audit logs (limited to recent)
        const { data: auditLogs, error: auditError } = await supabase
          .from('audit_logs')
          .select('id, table_name, action, user_id, created_at')
          .or(`table_name.ilike.${searchTerm},action.ilike.${searchTerm}`)
          .order('created_at', { ascending: false })
          .limit(4);

        if (!auditError && auditLogs) {
          auditLogs.forEach(log => {
            results.push({
              type: 'audit',
              id: log.id,
              label: `${log.table_name} - ${log.action}`,
              sublabel: `${log.user_id || 'System'} - ${formatDateTime(log.created_at, '')}`,
              href: '/superadmin/audit',
              icon: 'Shield'
            });
          });
        }

        // Sort results by relevance (churches first, then profiles, etc.)
        const typeOrder = { church: 1, profile: 2, event: 3, audit: 4 };
        const sortedResults = results.sort((a, b) => {
          // First sort by type order
          const typeDiff = typeOrder[a.type] - typeOrder[b.type];
          if (typeDiff !== 0) return typeDiff;
          
          // Then by label relevance to query
          const aRelevance = a.label.toLowerCase().includes(query.toLowerCase()) ? 1 : 2;
          const bRelevance = b.label.toLowerCase().includes(query.toLowerCase()) ? 1 : 2;
          return aRelevance - bRelevance;
        });

        // Return max 25 results
        const finalResults = sortedResults.slice(0, 25);
        console.debug("[useGlobalSearch] found", finalResults.length, "results");
        return finalResults;

      } catch (error) {
        console.warn('Error performing global search:', error);
        return [];
      }
    },
    enabled: !!query && query.length >= 2,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnMount: true,
  });
}
